'use client'

import { Dialog, DialogContent } from '@/components/ui/dialog'
import { cn } from '@/utils/styles/cn'
import { RequireSome } from '@/utils/typings/require-some'
import { type DialogProps } from '@radix-ui/react-dialog'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { Command as CommandPrimitive } from 'cmdk'
import * as React from 'react'

const Command = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
    <CommandPrimitive
        ref={ref}
        className={cn(
            'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
            className
        )}
        {...props}
    />
))
Command.displayName = CommandPrimitive.displayName

type CommandDialogProps = RequireSome<DialogProps, 'open' | 'onOpenChange'>

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
    return (
        <Dialog {...props}>
            <DialogContent className="overflow-hidden p-0">
                <Command className="**:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    )
}

interface CommandInputProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> {
    variant?: 'default' | 'dark'
}

const CommandInput = React.forwardRef<HTMLInputElement, CommandInputProps>(
    ({ className, variant = 'default', ...props }, ref) => (
        <div
            className={cn(
                'flex items-center border-b px-3',
                variant === 'dark' ? 'border-[#FFFFFF14]' : 'border-border'
            )}
            cmdk-input-wrapper=""
        >
            <MagnifyingGlassIcon
                className={cn(
                    'mr-2 h-4 w-4 shrink-0',
                    variant === 'dark' ? 'text-[#FFFFFF99] opacity-100' : 'opacity-50'
                )}
            />
            <CommandPrimitive.Input
                ref={ref}
                className={cn(
                    `flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50`,
                    variant === 'dark'
                        ? 'text-[#FFFFFFF2] placeholder:text-[#FFFFFF99]'
                        : 'text-foreground placeholder:text-muted-foreground',
                    className
                )}
                {...props}
            />
        </div>
    )
)

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.List
        ref={ref}
        className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
        {...props}
    />
))

CommandList.displayName = CommandPrimitive.List.displayName

interface CommandEmptyProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty> {
    variant?: 'default' | 'dark'
}

const CommandEmpty = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Empty>, CommandEmptyProps>(
    ({ className, variant = 'default', ...props }, ref) => (
        <CommandPrimitive.Empty
            ref={ref}
            className={cn(
                'py-6 text-center text-sm',
                variant === 'dark' ? 'text-[#FFFFFF99]' : 'text-muted-foreground',
                className
            )}
            {...props}
        />
    )
)

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Group>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Group
        ref={ref}
        className={cn(
            `text-foreground **:[[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium`,
            className
        )}
        {...props}
    />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Separator ref={ref} className={cn('bg-border -mx-1 h-px', className)} {...props} />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

interface CommandItemProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> {
    variant?: 'default' | 'dark'
}

const CommandItem = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Item>, CommandItemProps>(
    ({ className, variant = 'default', ...props }, ref) => (
        <CommandPrimitive.Item
            ref={ref}
            className={cn(
                `relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50`,
                variant === 'dark'
                    ? 'text-[#FFFFFFF2] aria-selected:bg-linear-to-r aria-selected:from-[#313136] aria-selected:to-[#35353A] data-[selected=true]:bg-linear-to-r data-[selected=true]:from-[#313136] data-[selected=true]:to-[#35353A]'
                    : 'aria-selected:bg-accent aria-selected:text-accent-foreground',
                className
            )}
            {...props}
        />
    )
)

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return <span className={cn('text-muted-foreground ml-auto text-xs tracking-widest', className)} {...props} />
}
CommandShortcut.displayName = 'CommandShortcut'

export {
    Command,
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandShortcut,
    CommandSeparator,
}
